import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { buildAllTimeGreatsPath } from "../../constants/allTimeGreats";
import { serializeHeadshotIds } from "../../utils/playerHeadshots";
import { buildOpsRunsQuery } from "../../utils/opsRuns";
import { buildMlbSimilarSeasonsPath } from "../../components/similarSeasons/similarSeasonsQuery";

// Dynamic API base URL (same logic as utils/api.js)
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  const hostname = window.location.hostname;
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "http://localhost:8008/api";
  } else {
    return "/api";
  }
};

// Opportunity keys can contain `:` and other reserved characters, so they are
// escaped before landing in an Express `:id` path segment.
const socialOpportunityActionUrl = (id, action) =>
  `/social/opportunities/${encodeURIComponent(id)}/${action}`;

export const mlbApi = createApi({
  reducerPath: "mlbApi",
  baseQuery: fetchBaseQuery({
    baseUrl: getApiBaseUrl(),
    prepareHeaders: (headers, { getState }) => {
      headers.set("Content-Type", "application/json");
      // Add cache-busting header
      headers.set("Cache-Control", "no-cache");

      // Get token from localStorage
      const token = localStorage.getItem("token");
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }

      return headers;
    },
    responseHandler: async (response) => {
      const data = await response.json();

      // Handle admin access errors
      if (response.status === 403 && data.code === "ADMIN_REQUIRED") {
        // Redirect to home page
        window.location.href = "/app";
        return data;
      }

      return data;
    },
  }),
  tagTypes: [
    "Player",
    "Team",
    "QueryResult",
    "Leaderboard",
    "Admin",
    "OpsRuns",
    "Trivia",
    "TriviaDaily",
    "TriviaArtifacts",
    "Widget",
    "MLBScores",
    "FanPage",
    "FanProfile",
    "Social",
  ],
  endpoints: (builder) => ({
    // Get all players
    getPlayers: builder.query({
      query: () => "/players",
      providesTags: ["Player"],
    }),

    // Get player by ID
    getPlayerById: builder.query({
      query: (id) => `/players/${id}`,
      transformResponse: (response) => {
        // Map bioData back to bio for frontend compatibility
        if (response.bioData) {
          return {
            ...response,
            bio: response.bioData,
            bioData: undefined, // Remove bioData to avoid confusion
          };
        }
        return response;
      },
      providesTags: (result, error, id) => [{ type: "Player", id }],
    }),

    getPlayerByMlbId: builder.query({
      query: (mlbId) => `/players/by-mlb/${mlbId}`,
      providesTags: (result, error, mlbId) => [
        { type: "Player", id: result?.playerId || `mlb-${mlbId}` },
      ],
    }),

    getMlbSimilarSeasons: builder.query({
      query: ({
        playerId,
        season,
        role,
        limit = 8,
        excludeSamePlayer = true,
      } = {}) =>
        buildMlbSimilarSeasonsPath({
          playerId,
          season,
          role,
          limit,
          excludeSamePlayer,
        }),
    }),

    // Search players by name
    searchPlayers: builder.query({
      query: (searchTerm) => `/players/search?name=${searchTerm}`,
      transformResponse: (response) => {
        // Map bioData back to bio for each player in the array
        return response.map((player) => {
          if (player.bioData) {
            return {
              ...player,
              bio: player.bioData,
              bioData: undefined, // Remove bioData to avoid confusion
            };
          }
          return player;
        });
      },
      providesTags: ["Player"],
    }),

    // Get popular players
    getPopularPlayers: builder.query({
      query: (sortBy = "homeruns") => `/players/popular?sortBy=${sortBy}`,
      transformResponse: (response) => {
        // Map bioData back to bio for each player in the array
        return response.map((player) => {
          if (player.bioData) {
            return {
              ...player,
              bio: player.bioData,
              bioData: undefined, // Remove bioData to avoid confusion
            };
          }
          return player;
        });
      },
      providesTags: ["Player"],
    }),

    // Get top players for a category
    getTopPlayers: builder.query({
      query: (category) => `/top-players/${category}`,
      providesTags: ["Player"],
    }),

    // Get all teams with pagination
    getTeams: builder.query({
      query: ({ page = 1, pageSize = 25 } = {}) =>
        `/teams?page=${page}&pageSize=${pageSize}`,
      providesTags: ["Team"],
    }),

    // Get individual team by name
    getTeamByName: builder.query({
      query: (teamName) => `/teams/${teamName}`,
      providesTags: (result, error, teamName) => [
        { type: "Team", id: teamName },
      ],
    }),

    // QueryBuilder endpoint
    executeQuery: builder.query({
      query: (queryParams) => ({
        url: "/queryBuilder/query",
        method: "POST",
        body: queryParams,
      }),
      transformResponse: (response) => {
        if (response.results && Array.isArray(response.results)) {
          return {
            ...response,
            results: response.results.map((player) => ({
              ...player,
              bio: player.bio || player.bioData,
              bioData: player.bioData,
            })),
          };
        }
        return response;
      },
    }),

    previewQueryCount: builder.query({
      query: (queryParams) => ({
        url: "/queryBuilder/count",
        method: "POST",
        body: queryParams,
      }),
    }),

    // Get available stats for QueryBuilder
    getAvailableStats: builder.query({
      query: () => "/queryBuilder/available-stats",
      providesTags: ["QueryResult"],
    }),

    // Get available teams for QueryBuilder
    getAvailableTeams: builder.query({
      query: () => "/queryBuilder/available-teams",
      providesTags: ["QueryResult"],
    }),

    // All-Time Greats endpoints with pagination
    getHomeRunLeaders: builder.query({
      query: ({ page = 1, pageSize = 25 } = {}) =>
        `/all-time-greats/home-runs?page=${page}&pageSize=${pageSize}`,
      providesTags: ["Leaderboard"],
    }),

    getBattingAverageLeaders: builder.query({
      query: ({ page = 1, pageSize = 25, minAtBats = 3000 } = {}) =>
        `/all-time-greats/batting-average?page=${page}&pageSize=${pageSize}&minAtBats=${minAtBats}`,
      providesTags: ["Leaderboard"],
    }),

    getRbiLeaders: builder.query({
      query: ({ page = 1, pageSize = 25 } = {}) =>
        `/all-time-greats/rbi?page=${page}&pageSize=${pageSize}`,
      providesTags: ["Leaderboard"],
    }),

    getHitsLeaders: builder.query({
      query: ({ page = 1, pageSize = 25 } = {}) =>
        `/all-time-greats/hits?page=${page}&pageSize=${pageSize}`,
      providesTags: ["Leaderboard"],
    }),

    // New Hitting Stats Leaderboards
    getOnBasePercentageLeaders: builder.query({
      query: ({ page = 1, pageSize = 25, minAtBats = 3000 } = {}) =>
        `/all-time-greats/on-base-percentage?page=${page}&pageSize=${pageSize}&minAtBats=${minAtBats}`,
      providesTags: ["Leaderboard"],
    }),
    getSluggingAverageLeaders: builder.query({
      query: ({ page = 1, pageSize = 25, minAtBats = 3000 } = {}) =>
        `/all-time-greats/slugging-average?page=${page}&pageSize=${pageSize}&minAtBats=${minAtBats}`,
      providesTags: ["Leaderboard"],
    }),
    getRunsLeaders: builder.query({
      query: ({ page = 1, pageSize = 25 } = {}) =>
        `/all-time-greats/runs?page=${page}&pageSize=${pageSize}`,
      providesTags: ["Leaderboard"],
    }),
    getDoublesLeaders: builder.query({
      query: ({ page = 1, pageSize = 25 } = {}) =>
        `/all-time-greats/doubles?page=${page}&pageSize=${pageSize}`,
      providesTags: ["Leaderboard"],
    }),
    getTriplesLeaders: builder.query({
      query: ({ page = 1, pageSize = 25 } = {}) =>
        `/all-time-greats/triples?page=${page}&pageSize=${pageSize}`,
      providesTags: ["Leaderboard"],
    }),
    getWalksLeaders: builder.query({
      query: ({ page = 1, pageSize = 25 } = {}) =>
        `/all-time-greats/walks?page=${page}&pageSize=${pageSize}`,
      providesTags: ["Leaderboard"],
    }),
    getStrikeoutsLeaders: builder.query({
      query: ({ page = 1, pageSize = 25 } = {}) =>
        `/all-time-greats/strikeouts?page=${page}&pageSize=${pageSize}`,
      providesTags: ["Leaderboard"],
    }),

    // Pitching Stats Leaderboards
    getWinsLeaders: builder.query({
      query: ({ page = 1, pageSize = 25 } = {}) =>
        `/all-time-greats/wins?page=${page}&pageSize=${pageSize}`,
      providesTags: ["Leaderboard"],
    }),
    getEraLeaders: builder.query({
      query: ({ page = 1, pageSize = 25, minInnings = 1000 } = {}) =>
        `/all-time-greats/era?page=${page}&pageSize=${pageSize}&minInnings=${minInnings}`,
      providesTags: ["Leaderboard"],
    }),
    getPitchingStrikeoutsLeaders: builder.query({
      query: ({ page = 1, pageSize = 25 } = {}) =>
        `/all-time-greats/pitching-strikeouts?page=${page}&pageSize=${pageSize}`,
      providesTags: ["Leaderboard"],
    }),
    getPitchingWalksLeaders: builder.query({
      query: ({ page = 1, pageSize = 25 } = {}) =>
        `/all-time-greats/pitching-walks?page=${page}&pageSize=${pageSize}`,
      providesTags: ["Leaderboard"],
    }),
    getHitsAllowedLeaders: builder.query({
      query: ({ page = 1, pageSize = 25 } = {}) =>
        `/all-time-greats/hits-allowed?page=${page}&pageSize=${pageSize}`,
      providesTags: ["Leaderboard"],
    }),
    getEarnedRunsLeaders: builder.query({
      query: ({ page = 1, pageSize = 25 } = {}) =>
        `/all-time-greats/earned-runs?page=${page}&pageSize=${pageSize}`,
      providesTags: ["Leaderboard"],
    }),
    getCompleteGamesLeaders: builder.query({
      query: ({ page = 1, pageSize = 25 } = {}) =>
        `/all-time-greats/complete-games?page=${page}&pageSize=${pageSize}`,
      providesTags: ["Leaderboard"],
    }),
    getShutoutsLeaders: builder.query({
      query: ({ page = 1, pageSize = 25 } = {}) =>
        `/all-time-greats/shutouts?page=${page}&pageSize=${pageSize}`,
      providesTags: ["Leaderboard"],
    }),
    getSavesLeaders: builder.query({
      query: ({ page = 1, pageSize = 25 } = {}) =>
        `/all-time-greats/saves?page=${page}&pageSize=${pageSize}`,
      providesTags: ["Leaderboard"],
    }),
    getInningsPitchedLeaders: builder.query({
      query: ({ page = 1, pageSize = 25 } = {}) =>
        `/all-time-greats/innings-pitched?page=${page}&pageSize=${pageSize}`,
      providesTags: ["Leaderboard"],
    }),
    getHomeRunsAllowedLeaders: builder.query({
      query: ({ page = 1, pageSize = 25 } = {}) =>
        `/all-time-greats/home-runs-allowed?page=${page}&pageSize=${pageSize}`,
      providesTags: ["Leaderboard"],
    }),

    // Fielding Stats Leaderboards
    getFieldingGamesLeaders: builder.query({
      query: ({ page = 1, pageSize = 25 } = {}) =>
        `/all-time-greats/fielding-games?page=${page}&pageSize=${pageSize}`,
      providesTags: ["Leaderboard"],
    }),
    getChancesLeaders: builder.query({
      query: ({ page = 1, pageSize = 25 } = {}) =>
        `/all-time-greats/chances?page=${page}&pageSize=${pageSize}`,
      providesTags: ["Leaderboard"],
    }),
    getPutoutsLeaders: builder.query({
      query: ({ page = 1, pageSize = 25 } = {}) =>
        `/all-time-greats/putouts?page=${page}&pageSize=${pageSize}`,
      providesTags: ["Leaderboard"],
    }),
    getAssistsLeaders: builder.query({
      query: ({ page = 1, pageSize = 25 } = {}) =>
        `/all-time-greats/assists?page=${page}&pageSize=${pageSize}`,
      providesTags: ["Leaderboard"],
    }),
    getErrorsLeaders: builder.query({
      query: ({ page = 1, pageSize = 25 } = {}) =>
        `/all-time-greats/errors?page=${page}&pageSize=${pageSize}`,
      providesTags: ["Leaderboard"],
    }),
    getFieldingPercentageLeaders: builder.query({
      query: ({ page = 1, pageSize = 25, minChances = 1000 } = {}) =>
        `/all-time-greats/fielding-percentage?page=${page}&pageSize=${pageSize}&minChances=${minChances}`,
      providesTags: ["Leaderboard"],
    }),
    getRangeFactorLeaders: builder.query({
      query: ({ page = 1, pageSize = 25, minGames = 500 } = {}) =>
        `/all-time-greats/range-factor?page=${page}&pageSize=${pageSize}&minGames=${minGames}`,
      providesTags: ["Leaderboard"],
    }),

    getAllTimeGreatsLeaders: builder.query({
      query: ({ stat, page = 1, pageSize = 25 } = {}) =>
        buildAllTimeGreatsPath({ stat, page, pageSize }),
      providesTags: ["Leaderboard"],
    }),

    getPlayerHeadshots: builder.query({
      query: (ids) => `/players/headshots?ids=${serializeHeadshotIds(ids)}`,
      transformResponse: (response) => response.headshots || {},
      keepUnusedDataFor: 86400,
    }),

    // MLB Schedule & Scores endpoints
    getTodaysScores: builder.query({
      query: () => "/schedule/today",
      providesTags: ["MLBScores"],
      // Cache for 5 minutes on frontend
      keepUnusedDataFor: 300,
    }),

    getScoresByDate: builder.query({
      query: (date) => `/schedule/date/${date}`,
      providesTags: ["MLBScores"],
      keepUnusedDataFor: 300,
    }),

    getTeamSchedule: builder.query({
      query: ({ teamId, startDate, endDate }) =>
        `/schedule/team/${teamId}?startDate=${startDate}&endDate=${endDate}`,
      providesTags: ["MLBScores"],
      keepUnusedDataFor: 300,
    }),

    getScheduleHealth: builder.query({
      query: () => "/schedule/health",
      providesTags: ["MLBScores"],
    }),

    getGameDetails: builder.query({
      query: (gamePk) => `/schedule/game/${gamePk}`,
      providesTags: (result, error, gamePk) => [{ type: "MLBScores", id: `game-${gamePk}` }],
      keepUnusedDataFor: 300,
    }),

    // Admin endpoints
    getAdminStats: builder.query({
      query: () => "/admin/stats",
      providesTags: ["Admin"],
    }),

    getAdminUsers: builder.query({
      query: ({ page = 1, pageSize = 25 } = {}) =>
        `/admin/users?page=${page}&pageSize=${pageSize}`,
      providesTags: ["Admin"],
    }),

    searchAdminUsers: builder.query({
      query: (searchQuery) => `/admin/users/search/${searchQuery}`,
      providesTags: ["Admin"],
    }),

    elevateUserToAdmin: builder.mutation({
      query: (userId) => ({
        url: `/admin/users/${userId}/elevate`,
        method: "POST",
      }),
      invalidatesTags: ["Admin"],
    }),

    demoteUserFromAdmin: builder.mutation({
      query: (userId) => ({
        url: `/admin/users/${userId}/demote`,
        method: "POST",
      }),
      invalidatesTags: ["Admin"],
    }),

    getOpsRuns: builder.query({
      query: (filters = {}) => buildOpsRunsQuery(filters),
      providesTags: ["Admin", "OpsRuns"],
    }),
    getOpsRun: builder.query({
      query: (id) => `/admin/ops/runs/${id}`,
      providesTags: (_result, _error, id) => [{ type: "OpsRuns", id }],
    }),

    // Social command-center endpoints
    getSocialOpportunities: builder.query({
      query: (filters = {}) => {
        const params = new URLSearchParams();
        ["sport", "status", "type"].forEach((key) => {
          if (filters[key]) params.set(key, filters[key]);
        });
        const query = params.toString();
        return `/social/opportunities${query ? `?${query}` : ""}`;
      },
      providesTags: ["Social"],
    }),
    refreshSocialOpportunities: builder.mutation({
      query: (body = {}) => ({
        url: "/social/opportunities/refresh",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Social"],
    }),
    createManualSocialOpportunity: builder.mutation({
      query: (body) => ({
        url: "/social/opportunities/manual",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Social"],
    }),
    dismissSocialOpportunity: builder.mutation({
      query: (id) => ({
        url: socialOpportunityActionUrl(id, "dismiss"),
        method: "POST",
      }),
      invalidatesTags: ["Social"],
    }),
    saveSocialOpportunity: builder.mutation({
      query: (id) => ({
        url: socialOpportunityActionUrl(id, "save"),
        method: "POST",
      }),
      invalidatesTags: ["Social"],
    }),
    useSocialOpportunity: builder.mutation({
      query: (id) => ({
        url: socialOpportunityActionUrl(id, "use"),
        method: "POST",
      }),
      invalidatesTags: ["Social"],
    }),
    createSocialPost: builder.mutation({
      query: (body) => ({
        url: "/social/posts",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Social"],
    }),
    getSocialPost: builder.query({
      query: (id) => `/social/posts/${id}`,
      providesTags: ["Social"],
    }),
    getSocialPosts: builder.query({
      query: ({ status } = {}) =>
        `/social/posts${status ? `?status=${encodeURIComponent(status)}` : ""}`,
      providesTags: ["Social"],
    }),
    generateSocialPost: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/social/posts/${id}/generate`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Social"],
    }),
    exportSocialPost: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/social/posts/${id}/export`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Social"],
    }),
    markSocialPostPosted: builder.mutation({
      query: (id) => ({
        url: `/social/posts/${id}/posted`,
        method: "POST",
      }),
      invalidatesTags: ["Social"],
    }),
    patchSocialPost: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/social/posts/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["Social"],
    }),

    // Trivia endpoints
    getQuestionTypes: builder.query({
      query: () => "/trivia/question-types",
      providesTags: ["Trivia"],
    }),

    getQuestionCategories: builder.query({
      query: () => "/trivia/question-categories",
      providesTags: ["Trivia"],
    }),

    // New dynamic generation endpoints
    getDynamicCategories: builder.query({
      query: () => "/trivia/categories",
      providesTags: ["Trivia"],
    }),

    getDynamicSubcategories: builder.query({
      query: (categoryId) => `/trivia/categories/${categoryId}/subcategories`,
      providesTags: ["Trivia"],
    }),

    generateDynamicQuestions: builder.mutation({
      query: (params) => ({
        url: "/trivia/generate",
        method: "POST",
        body: params,
      }),
      invalidatesTags: ["Trivia"],
    }),

    saveQuestions: builder.mutation({
      query: (questions) => ({
        url: "/trivia/save",
        method: "POST",
        body: { questions },
      }),
      invalidatesTags: ["Trivia"],
    }),

    // Difficulty configuration endpoints
    getDifficultyConfigs: builder.query({
      query: () => "/trivia/difficulty-configs",
      providesTags: ["DifficultyConfigs"],
    }),
    getDifficultyConfig: builder.query({
      query: (configId) => `/trivia/difficulty-configs/${configId}`,
      providesTags: ["DifficultyConfig"],
    }),
    getDefaultDifficultyConfig: builder.query({
      query: () => "/trivia/difficulty-configs/default",
      providesTags: ["DefaultDifficultyConfig"],
    }),

    getTriviaTemplates: builder.query({
      query: () => "/trivia/admin/templates",
      providesTags: ["Trivia"],
    }),

    getTriviaTemplate: builder.query({
      query: (templateId) => `/trivia/admin/templates/${templateId}`,
      providesTags: ["Trivia"],
    }),

    getTriviaStats: builder.query({
      query: () => "/trivia/admin/stats",
      providesTags: ["Trivia"],
    }),

    getTriviaQuestions: builder.query({
      query: ({ page = 1, pageSize = 25 } = {}) =>
        `/trivia/admin/questions?page=${page}&pageSize=${pageSize}`,
      providesTags: (result) =>
        result
          ? [
              ...result.questions.map(({ _id }) => ({
                type: "Trivia",
                id: _id,
              })),
              { type: "Trivia", id: "LIST" },
            ]
          : [{ type: "Trivia", id: "LIST" }],
    }),

    generateTriviaQuestions: builder.mutation({
      query: (params) => ({
        url: "/trivia/admin/generate",
        method: "POST",
        body: params,
      }),
      invalidatesTags: ["Trivia"],
    }),

    deactivateTriviaQuestions: builder.mutation({
      query: (questionIds) => ({
        url: "/trivia/admin/questions/deactivate",
        method: "PUT",
        body: { questionIds },
      }),
      invalidatesTags: ["Trivia"],
    }),

    activateTriviaQuestions: builder.mutation({
      query: (questionIds) => ({
        url: "/trivia/admin/questions/activate",
        method: "PUT",
        body: { questionIds },
      }),
      invalidatesTags: ["Trivia"],
    }),

    deleteTriviaQuestions: builder.mutation({
      query: (questionIds) => ({
        url: "/trivia/admin/questions/delete",
        method: "DELETE",
        body: { questionIds },
      }),
      invalidatesTags: (result, error, questionIds) => [
        { type: "Trivia", id: "LIST" },
        ...questionIds.map((id) => ({ type: "Trivia", id })),
      ],
    }),

    // Template CRUD mutations
    createTriviaTemplate: builder.mutation({
      query: (template) => ({
        url: "/trivia/admin/templates",
        method: "POST",
        body: template,
      }),
      invalidatesTags: ["Trivia"],
    }),

    updateTriviaTemplate: builder.mutation({
      query: ({ templateId, ...template }) => ({
        url: `/trivia/admin/templates/${templateId}`,
        method: "PUT",
        body: template,
      }),
      invalidatesTags: ["Trivia"],
    }),

    deleteTriviaTemplate: builder.mutation({
      query: (templateId) => ({
        url: `/trivia/admin/templates/${templateId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Trivia"],
    }),

    // Public trivia endpoint
    getTriviaGameQuestions: builder.query({
      query: ({ category, difficulty, count = 10 } = {}) => {
        const params = new URLSearchParams();
        if (category) params.append("category", category);
        if (difficulty) params.append("difficulty", difficulty);
        if (count) params.append("count", count);

        return {
          url: `/trivia/game?${params.toString()}`,
        };
      },
      providesTags: ["Trivia"],
    }),

    // New Trivia Game Session Endpoints
    startTriviaGame: builder.mutation({
      query: (gameConfig) => ({
        url: "/trivia/game/start",
        method: "POST",
        body: gameConfig,
      }),
      invalidatesTags: ["Trivia"],
    }),

    getTriviaGameSession: builder.query({
      query: (sessionId) => {
        if (!sessionId || sessionId === null || sessionId === undefined) {
          throw new Error("Invalid session ID");
        }
        return `/trivia/game/${sessionId}`;
      },
      providesTags: (result, error, sessionId) => [
        { type: "Trivia", id: `session-${sessionId}` },
      ],
    }),

    submitTriviaAnswer: builder.mutation({
      query: ({ sessionId, answerData }) => ({
        url: `/trivia/game/${sessionId}/answer`,
        method: "POST",
        body: answerData,
      }),
      invalidatesTags: (result, error, { sessionId }) => [
        { type: "Trivia", id: `session-${sessionId}` },
      ],
    }),

    completeTriviaGame: builder.mutation({
      query: (sessionId) => ({
        url: `/trivia/game/${sessionId}/complete`,
        method: "POST",
      }),
      invalidatesTags: ["Trivia"],
    }),

    shareTriviaGame: builder.mutation({
      query: (sessionId) => ({
        url: `/trivia/game/${sessionId}/share`,
        method: "POST",
      }),
      invalidatesTags: ["Trivia"],
    }),

    abandonTriviaGame: builder.mutation({
      query: (sessionId) => ({
        url: `/trivia/game/${sessionId}/abandon`,
        method: "DELETE",
      }),
      invalidatesTags: ["Trivia"],
    }),

    getTriviaGameHistory: builder.query({
      query: ({ page = 1, pageSize = 10 } = {}) =>
        `/trivia/game/history?page=${page}&pageSize=${pageSize}`,
      providesTags: ["Trivia"],
    }),

    // Trivia User Stats and Achievements
    getTriviaUserStats: builder.query({
      query: () => "/trivia/user/stats",
      providesTags: ["Trivia"],
    }),

    getTriviaUserAchievements: builder.query({
      query: () => "/trivia/achievements",
      providesTags: ["Trivia"],
    }),

    // Trivia Simulator
    getTriviaSimulatorQuestion: builder.mutation({
      query: ({ category, difficulty, questionType }) => ({
        url: "/trivia/simulator/question",
        method: "POST",
        body: {
          category,
          difficulty,
          questionType: questionType === "all" ? null : questionType,
        },
      }),
    }),

    // Trivia Leaderboards
    getTriviaLeaderboards: builder.query({
      query: ({ type = "global", forceRefresh = false } = {}) => {
        const params = new URLSearchParams();
        if (forceRefresh) params.append("forceRefresh", "true");
        return `/trivia/leaderboards/${type}?${params.toString()}`;
      },
      providesTags: ["Trivia"],
    }),

    getTriviaCategoryLeaderboard: builder.query({
      query: ({ category, forceRefresh = false } = {}) => {
        const params = new URLSearchParams();
        if (forceRefresh) params.append("forceRefresh", "true");
        return `/trivia/leaderboards/category/${category}?${params.toString()}`;
      },
      providesTags: ["Trivia"],
    }),

    getTriviaDifficultyLeaderboard: builder.query({
      query: ({ difficulty, forceRefresh = false } = {}) => {
        const params = new URLSearchParams();
        if (forceRefresh) params.append("forceRefresh", "true");
        return `/trivia/leaderboards/difficulty/${difficulty}?${params.toString()}`;
      },
      providesTags: ["Trivia"],
    }),

    getTriviaStreakLeaderboard: builder.query({
      query: ({ forceRefresh = false } = {}) => {
        const params = new URLSearchParams();
        if (forceRefresh) params.append("forceRefresh", "true");
        return `/trivia/leaderboards/streaks?${params.toString()}`;
      },
      providesTags: ["Trivia"],
    }),

    getTriviaSpeedLeaderboard: builder.query({
      query: ({ forceRefresh = false } = {}) => {
        const params = new URLSearchParams();
        if (forceRefresh) params.append("forceRefresh", "true");
        return `/trivia/leaderboards/speed?${params.toString()}`;
      },
      providesTags: ["Trivia"],
    }),

    getTriviaDaily: builder.query({
      query: (sport = "mlb") =>
        `/trivia/daily?sport=${encodeURIComponent(sport)}`,
      providesTags: ["TriviaDaily"],
    }),

    startTriviaDailyGame: builder.mutation({
      query: ({ sport = "mlb" } = {}) => ({
        url: "/trivia/daily/start",
        method: "POST",
        body: { sport },
      }),
      invalidatesTags: ["TriviaDaily", "Trivia"],
    }),

    getTriviaDailyLeaderboard: builder.query({
      query: (sport = "mlb") =>
        `/trivia/daily/leaderboard?sport=${encodeURIComponent(sport)}`,
      providesTags: ["TriviaDaily"],
    }),

    getTriviaAchievementCatalog: builder.query({
      query: () => "/trivia/achievements/catalog",
      providesTags: ["Trivia"],
    }),

    previewTriviaEngine: builder.mutation({
      query: (params) => ({
        url: "/trivia/engine/preview",
        method: "POST",
        body: params,
      }),
    }),

    getTriviaEnginePreset: builder.query({
      query: (sport = "mlb") =>
        `/trivia/engine/presets?sport=${encodeURIComponent(sport)}`,
      providesTags: ["Trivia"],
    }),

    saveTriviaEnginePreset: builder.mutation({
      query: (preset) => ({
        url: "/trivia/engine/presets",
        method: "PUT",
        body: preset,
      }),
      invalidatesTags: ["Trivia"],
    }),

    getTriviaArtifacts: builder.query({
      query: ({ sport, status, origin, page = 1, pageSize = 25 } = {}) => {
        const params = new URLSearchParams({
          page: String(page),
          pageSize: String(pageSize),
        });
        if (sport) params.set("sport", sport);
        if (status) params.set("status", status);
        if (origin) params.set("origin", origin);
        return `/trivia/admin/artifacts?${params.toString()}`;
      },
      providesTags: (result) =>
        result?.artifacts
          ? [
              ...result.artifacts.map(({ _id }) => ({
                type: "TriviaArtifacts",
                id: _id,
              })),
              { type: "TriviaArtifacts", id: "LIST" },
            ]
          : [{ type: "TriviaArtifacts", id: "LIST" }],
    }),

    getTriviaArtifact: builder.query({
      query: (id) => `/trivia/admin/artifacts/${id}?difficulty=medium`,
      providesTags: (result, error, id) => [{ type: "TriviaArtifacts", id }],
    }),

    archiveTriviaArtifact: builder.mutation({
      query: ({ id, reason, note }) => ({
        url: `/trivia/admin/artifacts/${id}/archive`,
        method: "POST",
        body: { reason, note },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "TriviaArtifacts", id },
        { type: "TriviaArtifacts", id: "LIST" },
      ],
    }),

    restoreTriviaArtifact: builder.mutation({
      query: ({ id }) => ({
        url: `/trivia/admin/artifacts/${id}/restore`,
        method: "POST",
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "TriviaArtifacts", id },
        { type: "TriviaArtifacts", id: "LIST" },
      ],
    }),

    // Widget endpoints
    getDashboard: builder.query({
      query: (dashboardType) => `/widgets/dashboard/${dashboardType}`,
      providesTags: (result, error, dashboardType) => [
        { type: "Widget", id: `dashboard-${dashboardType}` },
      ],
    }),

    saveDashboard: builder.mutation({
      query: ({ dashboardType, layout, gridConfig }) => ({
        url: `/widgets/dashboard/${dashboardType}`,
        method: "POST",
        body: { layout, gridConfig },
      }),
      invalidatesTags: (result, error, { dashboardType }) => [
        { type: "Widget", id: `dashboard-${dashboardType}` },
      ],
    }),

    getWidgetData: builder.query({
      query: ({ dataSource, timeRange = "month", forceRefresh = false }) => {
        const params = new URLSearchParams();
        if (timeRange) params.append("timeRange", timeRange);
        if (forceRefresh) params.append("forceRefresh", forceRefresh);

        return {
          url: `/widgets/data/${dataSource}?${params.toString()}`,
        };
      },
      providesTags: (result, error, { dataSource, timeRange }) => [
        { type: "Widget", id: `data-${dataSource}-${timeRange}` },
      ],
    }),

    getWidgetTemplates: builder.query({
      query: ({ dashboardType, category }) => {
        const params = new URLSearchParams();
        if (dashboardType) params.append("dashboardType", dashboardType);
        if (category) params.append("category", category);

        return {
          url: `/widgets/templates?${params.toString()}`,
        };
      },
      providesTags: ["Widget"],
    }),

    // Admin widget endpoints
    createWidgetTemplate: builder.mutation({
      query: (template) => ({
        url: "/widgets/admin/templates",
        method: "POST",
        body: template,
      }),
      invalidatesTags: ["Widget"],
    }),

    updateWidgetTemplate: builder.mutation({
      query: ({ templateId, ...template }) => ({
        url: `/widgets/admin/templates/${templateId}`,
        method: "PUT",
        body: template,
      }),
      invalidatesTags: ["Widget"],
    }),

    deleteWidgetTemplate: builder.mutation({
      query: (templateId) => ({
        url: `/widgets/admin/templates/${templateId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Widget"],
    }),

    clearWidgetCache: builder.mutation({
      query: () => ({
        url: "/widgets/admin/cache/clear",
        method: "POST",
      }),
      invalidatesTags: ["Widget"],
    }),

    getMyFanProfile: builder.query({
      query: () => "/profile/me",
      providesTags: ["FanProfile"],
    }),

    patchFanProfile: builder.mutation({
      query: (body) => ({
        url: "/profile/me",
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["FanProfile"],
    }),

    replaceFanProfileTeams: builder.mutation({
      query: (favoriteTeams) => ({
        url: "/profile/me/teams",
        method: "PUT",
        body: { favoriteTeams },
      }),
      invalidatesTags: ["FanProfile"],
    }),

    replaceFanProfileFollows: builder.mutation({
      query: (followedPlayers) => ({
        url: "/profile/me/follows",
        method: "PUT",
        body: { followedPlayers },
      }),
      invalidatesTags: ["FanProfile"],
    }),

    addFanProfileComparison: builder.mutation({
      query: (body) => ({
        url: "/profile/me/comparisons",
        method: "POST",
        body,
      }),
      invalidatesTags: ["FanProfile"],
    }),

    deleteFanProfileComparison: builder.mutation({
      query: (id) => ({
        url: `/profile/me/comparisons/${encodeURIComponent(id)}`,
        method: "DELETE",
      }),
      invalidatesTags: ["FanProfile"],
    }),

    getMyFanPage: builder.query({
      query: () => "/fan-pages/me",
      transformResponse: (response) => response.page,
      providesTags: ["FanPage"],
    }),

    getFanPageByHandle: builder.query({
      query: (handle) => `/fan-pages/${encodeURIComponent(handle)}`,
      transformResponse: (response) => response.page,
      providesTags: (_result, _error, handle) => [
        { type: "FanPage", id: handle },
      ],
    }),

    getTriviaShareResult: builder.query({
      query: (token) => `/trivia/share/${encodeURIComponent(token)}`,
      transformResponse: (response) => response.result,
    }),

    checkFanPageHandle: builder.query({
      query: (handle) =>
        `/fan-pages/handle-available?handle=${encodeURIComponent(handle)}`,
    }),

    saveFanPageLineup: builder.mutation({
      query: (lineup) => ({
        url: "/fan-pages/me/lineup",
        method: "PUT",
        body: { lineup },
      }),
      invalidatesTags: ["FanPage"],
    }),

    saveFanPageBattingOrder: builder.mutation({
      query: (battingOrder) => ({
        url: "/fan-pages/me/batting-order",
        method: "PUT",
        body: { battingOrder },
      }),
      invalidatesTags: ["FanPage"],
    }),

    saveFanPageFavorites: builder.mutation({
      query: (favorites) => ({
        url: "/fan-pages/me/favorites",
        method: "PUT",
        body: { favorites },
      }),
      invalidatesTags: ["FanPage"],
    }),

    publishFanPage: builder.mutation({
      query: (handle) => ({
        url: "/fan-pages/me/publish",
        method: "POST",
        body: { handle },
      }),
      invalidatesTags: ["FanPage"],
    }),

    getPositionLeaders: builder.query({
      query: ({ slot, sort } = {}) => {
        const params = sort ? `?sort=${encodeURIComponent(sort)}` : "";
        return `/players/position-leaders/${encodeURIComponent(slot)}${params}`;
      },
      providesTags: (_result, _error, arg) => [
        { type: "Player", id: `position-leaders-${arg?.slot}-${arg?.sort || "default"}` },
      ],
    }),
  }),
});

export const {
  useGetPlayersQuery,
  useGetPlayerByIdQuery,
  useGetPlayerByMlbIdQuery,
  useGetMlbSimilarSeasonsQuery,
  useSearchPlayersQuery,
  useGetPopularPlayersQuery,
  useGetTopPlayersQuery,
  useGetTeamsQuery,
  useGetTeamByNameQuery,
  useExecuteQueryQuery,
  usePreviewQueryCountQuery,
  useGetAvailableStatsQuery,
  useGetAvailableTeamsQuery,
  useGetHomeRunLeadersQuery,
  useGetBattingAverageLeadersQuery,
  useGetRbiLeadersQuery,
  useGetHitsLeadersQuery,
  // New Hitting Stats Leaderboard hooks
  useGetOnBasePercentageLeadersQuery,
  useGetSluggingAverageLeadersQuery,
  useGetRunsLeadersQuery,
  useGetDoublesLeadersQuery,
  useGetTriplesLeadersQuery,
  useGetWalksLeadersQuery,
  useGetStrikeoutsLeadersQuery,
  // Pitching Stats Leaderboard hooks
  useGetWinsLeadersQuery,
  useGetEraLeadersQuery,
  useGetPitchingStrikeoutsLeadersQuery,
  useGetPitchingWalksLeadersQuery,
  useGetHitsAllowedLeadersQuery,
  useGetEarnedRunsLeadersQuery,
  useGetCompleteGamesLeadersQuery,
  useGetShutoutsLeadersQuery,
  useGetSavesLeadersQuery,
  useGetInningsPitchedLeadersQuery,
  useGetHomeRunsAllowedLeadersQuery,
  // Fielding Stats Leaderboard hooks
  useGetFieldingGamesLeadersQuery,
  useGetChancesLeadersQuery,
  useGetPutoutsLeadersQuery,
  useGetAssistsLeadersQuery,
  useGetErrorsLeadersQuery,
  useGetFieldingPercentageLeadersQuery,
  useGetRangeFactorLeadersQuery,
  useGetAllTimeGreatsLeadersQuery,
  useGetPlayerHeadshotsQuery,
  // MLB Schedule & Scores hooks
  useGetTodaysScoresQuery,
  useGetScoresByDateQuery,
  useGetTeamScheduleQuery,
  useGetScheduleHealthQuery,
  useGetGameDetailsQuery,
  // Admin hooks
  useGetAdminStatsQuery,
  useGetAdminUsersQuery,
  useSearchAdminUsersQuery,
  useElevateUserToAdminMutation,
  useDemoteUserFromAdminMutation,
  useGetOpsRunsQuery,
  useGetOpsRunQuery,
  // Social hooks
  useGetSocialOpportunitiesQuery,
  useRefreshSocialOpportunitiesMutation,
  useCreateManualSocialOpportunityMutation,
  useDismissSocialOpportunityMutation,
  useSaveSocialOpportunityMutation,
  useUseSocialOpportunityMutation,
  useCreateSocialPostMutation,
  useGetSocialPostQuery,
  useGetSocialPostsQuery,
  useGenerateSocialPostMutation,
  useExportSocialPostMutation,
  useMarkSocialPostPostedMutation,
  usePatchSocialPostMutation,
  // Trivia hooks
  useGetQuestionTypesQuery,
  useGetQuestionCategoriesQuery,
  // New dynamic generation hooks
  useGetDynamicCategoriesQuery,
  useGetDynamicSubcategoriesQuery,
  useGenerateDynamicQuestionsMutation,
  useSaveQuestionsMutation,
  // Difficulty configuration hooks
  useGetDifficultyConfigsQuery,
  useGetDifficultyConfigQuery,
  useGetDefaultDifficultyConfigQuery,
  // Legacy trivia hooks
  useGetTriviaTemplatesQuery,
  useGetTriviaTemplateQuery,
  useGetTriviaStatsQuery,
  useGetTriviaQuestionsQuery,
  useGenerateTriviaQuestionsMutation,
  useGetTriviaSimulatorQuestionMutation,
  useDeactivateTriviaQuestionsMutation,
  useActivateTriviaQuestionsMutation,
  useDeleteTriviaQuestionsMutation,
  useCreateTriviaTemplateMutation,
  useUpdateTriviaTemplateMutation,
  useDeleteTriviaTemplateMutation,
  useGetTriviaGameQuestionsQuery,
  // New Trivia Game hooks
  useStartTriviaGameMutation,
  useGetTriviaGameSessionQuery,
  useSubmitTriviaAnswerMutation,
  useCompleteTriviaGameMutation,
  useShareTriviaGameMutation,
  useAbandonTriviaGameMutation,
  useGetTriviaGameHistoryQuery,
  useGetTriviaUserStatsQuery,
  useGetTriviaUserAchievementsQuery,
  useGetTriviaLeaderboardsQuery,
  useGetTriviaCategoryLeaderboardQuery,
  useGetTriviaDifficultyLeaderboardQuery,
  useGetTriviaStreakLeaderboardQuery,
  useGetTriviaSpeedLeaderboardQuery,
  useGetTriviaDailyQuery,
  useStartTriviaDailyGameMutation,
  useGetTriviaDailyLeaderboardQuery,
  useGetTriviaAchievementCatalogQuery,
  usePreviewTriviaEngineMutation,
  useGetTriviaEnginePresetQuery,
  useSaveTriviaEnginePresetMutation,
  useGetTriviaArtifactsQuery,
  useGetTriviaArtifactQuery,
  useArchiveTriviaArtifactMutation,
  useRestoreTriviaArtifactMutation,
  // Widget hooks
  useGetDashboardQuery,
  useSaveDashboardMutation,
  useGetWidgetDataQuery,
  useGetWidgetTemplatesQuery,
  useCreateWidgetTemplateMutation,
  useUpdateWidgetTemplateMutation,
  useDeleteWidgetTemplateMutation,
  useClearWidgetCacheMutation,
  useGetMyFanProfileQuery,
  usePatchFanProfileMutation,
  useReplaceFanProfileTeamsMutation,
  useReplaceFanProfileFollowsMutation,
  useAddFanProfileComparisonMutation,
  useDeleteFanProfileComparisonMutation,
  useGetMyFanPageQuery,
  useGetFanPageByHandleQuery,
  useGetTriviaShareResultQuery,
  useLazyCheckFanPageHandleQuery,
  useSaveFanPageLineupMutation,
  useSaveFanPageBattingOrderMutation,
  useSaveFanPageFavoritesMutation,
  usePublishFanPageMutation,
  useGetPositionLeadersQuery,
} = mlbApi;
